import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { Formik, Form, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { apiGet, apiPost, apiPut, apiDelete } from "helpers/apiService";
import SafeField from "helpers/SafeField";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";

const EmailTargetSubType = () => {
  const [list, setList] = useState([]);
  const [types, setTypes] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    fetchTypes();
    fetchList();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  /* ================= FETCH ================= */

  const fetchTypes = () => {
    apiGet("EmailTargetType/all")
      .then(res => setTypes(res.data))
      .catch(() => toast.error("Failed to load target types"));
  };

  const fetchList = () => {
    apiGet("EmailTargetSubType/all")
      .then(res => setList(res.data))
      .catch(() => toast.error("Failed to load sub types"));
  };

  /* ================= CREATE ================= */

  const handleCreate = (values, { setSubmitting, resetForm }) => {
    toast.loading("Saving...");

    apiPost("EmailTargetSubType", values)
      .then(() => {
        toast.dismiss();
        toast.success("Saved successfully");
        fetchList();
        resetForm();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Save failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= UPDATE ================= */

  const handleUpdate = (values, { setSubmitting }) => {
    toast.loading("Updating...");

    apiPut("EmailTargetSubType", values)
      .then(() => {
        toast.dismiss();
        toast.success("Updated successfully");
        fetchList();
        toggle();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      })
      .finally(() => setSubmitting(false));
  };

  /* ================= DELETE ================= */

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(result => {
      if (!result.isConfirmed) return;

      apiDelete(`EmailTargetSubType/${id}`)
        .then(() => {
          toast.success("Deleted successfully");
          fetchList();
        })
        .catch(() => toast.error("Delete failed"));
    });
  };

  const openEdit = item => {
    setSelected(item);
    setIsEdit(true);
    toggle();
  };

  /* ================= VALIDATION ================= */

  const validationSchema = Yup.object({
    emailTargetSubTypeTitle: Yup.string().required("Required"),
    emailTargetTypeId: Yup.number().required("Required"),
    emailTargetSubTypeObjectType: Yup.string().required("Required"),
    emailTargetSubTypeFilter: Yup.string().required("Required"),
  });

  return (
    <Container fluid className="page-content">
      <ToastContainer autoClose={2000} />
<Row className="mb20">
            <Col xl="12">
              <Breadcrumbs title="General Settings" breadcrumbItem="Email Target Sub Type" />
            </Col>
          </Row>

      <Row>
        <Col xl="12">
          {/* ===== ADD FORM ===== */}
          <Card>
            <CardBody>
             

              <Formik
                initialValues={{
                  emailTargetSubTypeTitle: "",
                  emailTargetTypeId: "",
                  emailTargetSubTypeObjectType: "",
                  emailTargetSubTypeFilter: "",
                  isActive: true,
                }}
                validationSchema={validationSchema}
                onSubmit={handleCreate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <Label>Title *</Label>
                          <SafeField
                            name="emailTargetSubTypeTitle"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.emailTargetSubTypeTitle && touched.emailTargetSubTypeTitle
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="emailTargetSubTypeTitle"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup>
                          <Label>Target Type *</Label>
                          <SafeField
                            name="emailTargetTypeId"
                            as="select"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.emailTargetTypeId && touched.emailTargetTypeId
                                ? "is-invalid"
                                : ""
                            }`}
                          >
                            <option value="">Select</option>
                            {types.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.emailTargetTypeTitle}
                              </option>
                            ))}
                          </SafeField>
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup>
                          <Label>Object Type *</Label>
                          <SafeField
                            name="emailTargetSubTypeObjectType"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.emailTargetSubTypeObjectType &&
                              touched.emailTargetSubTypeObjectType
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="3">
                        <FormGroup>
                          <Label>Filter *</Label>
                          <SafeField
                            name="emailTargetSubTypeFilter"
                            onChange={handleChange}
                            className={`form-control ${
                              errors.emailTargetSubTypeFilter &&
                              touched.emailTargetSubTypeFilter
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="2" className="d-flex align-items-end mt-2">
                        <Button type="submit" color="primary">
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>

          {/* ===== LIST ===== */}
          <Card>
            <CardBody>
              <h5>Sub Type List</h5>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Object</th>
                      <th>Filter</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>{i + 1}</td>
                        <td>{item.emailTargetSubTypeTitle}</td>
                        <td>{item.emailTargetTypeId || item.emailTargetType?.emailTargetTypeTitle}</td>
                        <td>{item.emailTargetSubTypeObjectType}</td>
                        <td>{item.emailTargetSubTypeFilter}</td>
                        <td>{item.isActive ? "Active" : "Inactive"}</td>
                        <td>
                            <ActionEdit onClick={() => openEdit(item)} />{" "}
                            <ActionDelete onClick={() => handleDelete(item.id)} />
                         
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ===== EDIT OFFCANVAS ===== */}
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader toggle={toggle}>Update Sub Type</OffcanvasHeader>
        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={selected}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
          >
            {({ handleChange }) => (
              <Form>
                <FormGroup>
                  <Label>Title *</Label>
                  <SafeField
                    name="emailTargetSubTypeTitle"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Target Type *</Label>
                  <SafeField
                    name="emailTargetTypeId"
                    as="select"
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">Select</option>
                    {types.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.emailTargetTypeTitle}
                      </option>
                    ))}
                  </SafeField>
                </FormGroup>

                <FormGroup>
                  <Label>Object Type *</Label>
                  <SafeField
                    name="emailTargetSubTypeObjectType"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Filter *</Label>
                  <SafeField
                    name="emailTargetSubTypeFilter"
                    onChange={handleChange}
                    className="form-control"
                  />
                </FormGroup>

                <FormGroup check className="mb-3">
                  <Field type="checkbox" name="isActive" />
                  <Label check className="ms-2">Active</Label>
                </FormGroup>

                <Button type="submit" color="primary">
                  Update
                </Button>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </Container>
  );
};

export default EmailTargetSubType;
